import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";

interface FaqItem {
  q: string;
  a: string;
  topic: string;
}

const WHATSAPP_URL = "https://wa.me/message/6X77HRWZGKPQH1";

const topics = [
  { key: "all", label: "Todos", icon: "ri-stack-line" },
  { key: "orders", label: "Pedidos", icon: "ri-shopping-bag-3-line" },
  { key: "shipping", label: "Envio", icon: "ri-truck-line" },
  { key: "returns", label: "Trocas", icon: "ri-refund-2-line" },
  { key: "care", label: "Cuidados", icon: "ri-hand-heart-line" },
  { key: "wholesale", label: "Atacado", icon: "ri-store-2-line" },
];

const faqs: FaqItem[] = [
  {
    topic: "orders",
    q: "Como acompanho meu pedido?",
    a: "Acesse sua conta, vá em Meus Pedidos e clique em qualquer pedido para ver o status atual. Enviaremos o código de rastreamento por e-mail assim que a etiqueta for gerada.",
  },
  {
    topic: "orders",
    q: "Posso alterar meu pedido depois de feito?",
    a: "A maioria dos pedidos é enviada em até 48 horas, então a janela é curta. Entre em contato pelo WhatsApp e faremos o possível.",
  },
  {
    topic: "shipping",
    q: "Quanto tempo demora a entrega?",
    a: "Capitais: 3 a 7 dias úteis. Interior: 5 a 12 dias úteis. Dependendo da transportadora na sua região.",
  },
  {
    topic: "shipping",
    q: "Vocês enviam para todo o Brasil?",
    a: "Sim — para todos os estados. O frete é calculado no checkout com base no seu CEP.",
  },
  {
    topic: "returns",
    q: "Qual é a política de trocas?",
    a: "Aceitamos trocas em até 30 dias após o recebimento, desde que a peça esteja sem uso. Itens personalizados não são elegíveis.",
  },
  {
    topic: "returns",
    q: "Eu pago o frete de devolução?",
    a: "Sim, exceto se o erro for nosso. Se alguma coisa chegar errada ou danificada, enviamos uma etiqueta de retorno sem custo.",
  },
  {
    topic: "care",
    q: "Como lavo as peças de linho?",
    a: "Água fria, ciclo delicado, secar ao ar. Evite alvejante. Ferro morno é permitido com o tecido levemente úmido.",
  },
  {
    topic: "care",
    q: "As peças de cerâmica vão ao lava-louças?",
    a: "Sim, mas recomendamos lavar à mão — preserva o esmalte por muito mais tempo.",
  },
  {
    topic: "wholesale",
    q: "Vocês atendem atacado?",
    a: "Trabalhamos com um número pequeno de lojas independentes. Entre em contato pelo WhatsApp com uma mensagem sobre o seu espaço.",
  },
];

const quickLinks = [
  { label: "Acompanhar pedido", icon: "ri-search-line", to: "/account/orders" },
  { label: "Iniciar troca", icon: "ri-arrow-go-back-line", to: "/account/orders" },
  { label: "Frete e envio", icon: "ri-truck-line", to: "#shipping" },
  { label: "Falar no WhatsApp", icon: "ri-whatsapp-line", to: WHATSAPP_URL, external: true },
];

export default function HelpPage() {
  const [topic, setTopic] = useState<string>("all");
  const [query, setQuery] = useState<string>("");
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return faqs.filter((f) => {
      const topicOk = topic === "all" ? true : f.topic === topic;
      const queryOk = q.length === 0 ? true : f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q);
      return topicOk && queryOk;
    });
  }, [topic, query]);

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900 flex flex-col">
      <Navbar />

      <main className="flex-1">
        <section className="w-full px-4 md:px-8 pt-10 md:pt-14 pb-10 bg-background-100/70 border-b border-background-200/70">
          <div className="max-w-[1100px] mx-auto">
            <div className="flex items-center gap-2 text-[12px] text-foreground-600 mb-4">
              <i className="ri-customer-service-2-line text-primary-500"></i>
              <span>Central de Ajuda</span>
              <span className="text-foreground-400">·</span>
              <span>Resposta em até 4h</span>
            </div>
            <h1 className="font-heading text-foreground-950 text-4xl md:text-5xl tracking-[-0.01em] mb-6">
              Como podemos ajudar?
            </h1>

            <div className="relative max-w-[640px]">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-500 text-[18px]"></i>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Pesquisar envio, trocas, cuidados…"
                className="w-full h-12 pl-11 pr-4 rounded-md bg-background-50 border border-background-300 focus:border-foreground-900 focus:outline-none text-[14px] text-foreground-900 placeholder:text-foreground-500 transition-colors"
              />
            </div>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
              {quickLinks.map((ql) =>
                ql.external ? (
                  <a
                    key={ql.label}
                    href={ql.to}
                    target="_blank"
                    rel="noreferrer"
                    className="group flex items-center gap-3 h-12 px-4 rounded-md bg-background-50 border border-background-200 hover:border-foreground-900 transition-colors cursor-pointer"
                  >
                    <span className="w-8 h-8 rounded-md bg-primary-100 flex items-center justify-center text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                      <i className={`${ql.icon} text-[16px]`}></i>
                    </span>
                    <span className="text-[13.5px] text-foreground-900 font-medium">{ql.label}</span>
                  </a>
                ) : (
                  <Link
                    key={ql.label}
                    to={ql.to}
                    className="group flex items-center gap-3 h-12 px-4 rounded-md bg-background-50 border border-background-200 hover:border-foreground-900 transition-colors cursor-pointer"
                  >
                    <span className="w-8 h-8 rounded-md bg-primary-100 flex items-center justify-center text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                      <i className={`${ql.icon} text-[16px]`}></i>
                    </span>
                    <span className="text-[13.5px] text-foreground-900 font-medium">{ql.label}</span>
                  </Link>
                )
              )}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-12 md:py-16">
          <div className="max-w-[1100px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">
            <aside className="lg:col-span-3">
              <p className="text-[11px] uppercase tracking-[0.22em] text-foreground-500 mb-3">
                Tópicos
              </p>
              <ul className="flex lg:flex-col gap-2 lg:gap-1 overflow-x-auto lg:overflow-visible">
                {topics.map((t) => {
                  const active = topic === t.key;
                  return (
                    <li key={t.key}>
                      <button
                        type="button"
                        onClick={() => setTopic(t.key)}
                        className={`w-full inline-flex items-center gap-2.5 h-10 px-3 rounded-md text-[13.5px] whitespace-nowrap cursor-pointer transition-colors ${
                          active
                            ? "bg-foreground-950 text-background-50"
                            : "bg-background-50 text-foreground-800 hover:bg-background-100"
                        }`}
                      >
                        <i className={`${t.icon} text-[16px]`}></i>
                        {t.label}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>

            <div className="lg:col-span-9">
              <div className="flex items-center justify-between mb-4">
                <p className="text-[12px] text-foreground-600">
                  {filtered.length} {filtered.length === 1 ? "resposta" : "respostas"}
                </p>
              </div>

              {filtered.length === 0 ? (
                <div className="border border-dashed border-background-300 rounded-md p-10 text-center">
                  <i className="ri-search-eye-line text-foreground-400 text-3xl"></i>
                  <p className="mt-3 text-[14px] text-foreground-700">
                    Nenhum resultado. Tente outra palavra-chave ou fale no WhatsApp.
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-background-200/70 border-y border-background-200/70">
                  {filtered.map((f, i) => {
                    const open = openIndex === i;
                    return (
                      <li key={f.q}>
                        <button
                          type="button"
                          onClick={() => setOpenIndex(open ? null : i)}
                          className="w-full flex items-start gap-4 py-5 text-left cursor-pointer group"
                        >
                          <span className="mt-0.5 font-mono text-[12px] text-foreground-400 w-8 shrink-0">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          <span className="flex-1 text-[15.5px] md:text-[16px] text-foreground-950 font-medium group-hover:text-primary-600 transition-colors">
                            {f.q}
                          </span>
                          <span
                            className={`w-7 h-7 rounded-full border border-background-300 flex items-center justify-center text-foreground-700 transition-transform ${
                              open ? "rotate-45 bg-foreground-950 text-background-50 border-foreground-950" : ""
                            }`}
                          >
                            <i className="ri-add-line text-[14px]"></i>
                          </span>
                        </button>
                        {open && (
                          <div className="pl-12 pr-10 pb-6 text-[14.5px] leading-relaxed text-foreground-700">
                            {f.a}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 pb-20">
          <div className="max-w-[1100px] mx-auto">
            <div className="rounded-lg bg-secondary-100/70 border border-background-200/70 p-8 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-foreground-500 mb-2">
                  Ainda com dúvida?
                </p>
                <p className="font-heading text-foreground-950 text-2xl md:text-3xl tracking-[-0.01em]">
                  Fale com a gente.
                </p>
                <p className="mt-2 text-[14px] text-foreground-700">
                  Respondemos em poucas horas nos dias úteis — geralmente antes disso.
                </p>
              </div>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-foreground-950 hover:bg-foreground-800 text-background-50 text-[14px] font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line"></i>
                Falar no WhatsApp
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}